import React from 'react';

import { formatDate, formatMoney, toMoney } from '@betterment/core';
import { Icon } from '@betterment/samba-react';
import { Flex, Text, Tooltip, theme } from '@betterment/style-closet-react';
import styled from '@emotion/styled';

import { DetailedTransaction } from '../../data';
import { showEstimatedAvailability } from '../../models/transaction';

const AvailabilitySchedule = styled.ul`
  list-style-type: none;
  padding: 0;
  margin: 0;
`;

const AvailabilityItem = styled.li`
  margin-bottom: ${theme.space[2]};

  &:last-of-type {
    margin-bottom: 0;
  }
`;

const getAvailabilityMessage = (transaction: DetailedTransaction) => {
  if (transaction.bettermentCategory === 'Merchandise Return') {
    return 'Returns typically take approximately 7 business days to complete.';
  }
  if (transaction.bettermentCategory === 'Check Deposit') {
    return 'Most check deposits take 1-2 business days to be made available. However, in some cases, they may take up to 7 business days to allow us to verify that the check is in good order. We’ll notify you as funds become available.';
  }
  return 'Deposits take about 4-5 business days to complete.';
};

interface Props {
  transaction: DetailedTransaction;
}

const Availability: React.FC<React.PropsWithChildren<Props>> = ({ transaction }) => {
  const availabilityMessage = getAvailabilityMessage(transaction);
  if (!showEstimatedAvailability(transaction)) {
    return null;
  }

  const color = 'bmtTeal90';

  return (
    <Flex py={8} px={[0, 6]} flexDirection="column">
      {transaction.depositAvailability?.amounts ? (
        <>
          <Flex justifyContent="flex-start" mb="3">
            <Text lineHeight="2" mr="1">
              Estimated availability
            </Text>
            <Tooltip label={availabilityMessage} title="more info about your deposit availability">
              <Icon name="QuestionMarkCircle" size="small" />
            </Tooltip>
          </Flex>
          {transaction.depositAvailability.amounts.length > 0 && (
            <AvailabilitySchedule data-testid="depositAvailabilitySchedule" aria-label="deposit availability schedule">
              {transaction.depositAvailability.amounts.map((amount, index, amounts) => (
                <AvailabilityItem
                  key={`availabilityAmounts:${amount.effectiveAvailabilityDate}`}
                  {...(index < amounts.length && { style: { marginBottom: '3' } })}
                >
                  <Text mr="6" fontSize="1" color={amount.isReleased ? color : 'bmtGrey50'}>
                    {formatDate(amount.effectiveAvailabilityDate, 'longOrdinalShortMonth')}
                  </Text>
                  <Text fontSize="1" color={amount.isReleased ? color : 'bmtGrey50'}>
                    {formatMoney(toMoney(amount.releaseAmountCents))}
                  </Text>
                </AvailabilityItem>
              ))}
            </AvailabilitySchedule>
          )}
        </>
      ) : (
        <Flex justifyContent="space-between">
          <Text lineHeight="2">Estimated availability</Text>
          <Flex alignItems="center">
            <Text fontWeight="normal" mr="1">
              {formatDate(transaction.availableAt, 'longOrdinalShortMonth')}
            </Text>
            <Tooltip label={availabilityMessage} title="more info about your deposit availability">
              <Icon name="QuestionMarkCircle" size="small" />
            </Tooltip>
          </Flex>
        </Flex>
      )}
    </Flex>
  );
};

export default Availability;
